// รายชื่อผู้ขาดสอบ — สารวัตรนักเรียนเอาไปตามต่อในหอพัก ฝ่ายวัดผล/วิชาการใช้ติดตามสอบซ่อม
// หนึ่งแถว = หนึ่งคนหนึ่งวิชา (ขาดสองวิชาขึ้นสองแถว เพราะสอบซ่อมรายวิชา)
//
// สารวัตร (16/27) เห็นได้เฉพาะระหว่างช่วงสอบ งานวัดผลและวิชาการสามัญเห็นทุกช่วงย้อนหลัง
// (ตรวจที่ server) หอพักอ่านจากทะเบียนหอ ณ ตอนนี้ — ที่ที่ต้องไปตามคือที่ที่เขานอนคืนนี้
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Form, Spinner, Table } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import { toISODate } from '../utils/thaiDate';
import { StudentAvatar } from '../teaching/attendanceParts';
import { API, STATUS, apiError, dayLabel, eventName, eventRange, pickEvent, useExamEvents } from './examAbsenceUtils';

const FILTERS = ['absent', 'leave', 'late'];

export default function ExamAbsentList() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [params, setParams] = useSearchParams();
  const { loading: evLoading, events, error: evError } = useExamEvents();
  const today = toISODate(new Date());
  const ev = useMemo(() => pickEvent(events, params.get('cal'), today), [events, params, today]);
  // ค่าตั้งต้นคือวันนี้ถ้าอยู่ในช่วงสอบ — สารวัตรเปิดมาเพื่อดูว่าวันนี้ใครขาด
  const dateParam = params.get('date');
  const date = ev
    ? (dateParam === 'all' ? null : ev.days.includes(dateParam) ? dateParam : ev.days.includes(today) ? today : null)
    : null;
  const statuses = (params.get('status') || 'absent').split(',').filter((s) => FILTERS.includes(s));
  const statusKey = statuses.join(',');

  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!ev) return undefined;
    let alive = true;
    setRows(null);
    setError(null);
    axiosInstance.get(`${API}/exam-absence/absent`, { params: { cal_id: ev.cal_id, date: date || undefined, status: statusKey || 'absent' } })
      .then(({ data }) => alive && setRows(data.rows))
      .catch((err) => alive && setError(apiError(err)));
    return () => { alive = false; };
  }, [ev, date, statusKey]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k); else next.set(k, String(v));
    }
    setParams(next);
  };

  const toggleStatus = (s) => {
    const set = new Set(statuses);
    if (set.has(s)) set.delete(s); else set.add(s);
    if (!set.size) set.add('absent');
    update({ status: FILTERS.filter((f) => set.has(f)).join(',') });
  };

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!rows) return [];
    if (!q) return rows;
    return rows.filter((r) => [r.st_name, r.st_arabicname, r.st_idnumber, r.room_label, r.subject_label, r.village_name, r.dorm_name]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
          <div>
            <h4 className="main-title mb-0">รายชื่อผู้ขาดสอบ</h4>
            <div className="fs-xs text-secondary">จากการเช็คของครูผู้คุมสอบ พร้อมที่พักในหอสำหรับติดตามตัว</div>
          </div>
          {rows && rows.length > 0 && (
            <Button size="sm" variant="outline-secondary" className="d-print-none" onClick={() => window.print()}>
              <i className="ri-printer-line"></i> พิมพ์
            </Button>
          )}
        </div>

        {evLoading && <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>}
        {evError && <Alert variant="danger">{evError}</Alert>}
        {!evLoading && !evError && !ev && (
          <Alert variant="secondary"><i className="ri-lock-line me-1"></i> ไม่อยู่ในช่วงสอบ — รายชื่อผู้ขาดสอบเปิดดูได้เฉพาะช่วงสอบ</Alert>
        )}

        {ev && (
          <Card className="card-one mb-3 d-print-none">
            <Card.Body className="p-3 d-flex flex-wrap align-items-center gap-2">
              {events.length > 1 ? (
                <Form.Select size="sm" style={{ maxWidth: 340 }} value={ev.cal_id}
                  onChange={(e) => update({ cal: e.target.value, date: null })}>
                  {events.map((e) => <option key={e.cal_id} value={e.cal_id}>{eventName(e)} ({eventRange(e)})</option>)}
                </Form.Select>
              ) : (
                <span className="fw-semibold me-2">{eventName(ev)}</span>
              )}
              <ButtonGroup size="sm" className="flex-wrap">
                <Button variant={!date ? 'primary' : 'outline-primary'} onClick={() => update({ date: 'all' })}>ทุกวัน</Button>
                {ev.days.map((d) => (
                  <Button key={d} variant={d === date ? 'primary' : 'outline-primary'} onClick={() => update({ date: d })}>
                    {dayLabel(d)}{d === today ? ' (วันนี้)' : ''}
                  </Button>
                ))}
              </ButtonGroup>
              <ButtonGroup size="sm">
                {FILTERS.map((s) => (
                  <Button key={s} variant={statuses.includes(s) ? STATUS[s].variant : `outline-${STATUS[s].variant}`} onClick={() => toggleStatus(s)}>
                    {STATUS[s].long}
                  </Button>
                ))}
              </ButtonGroup>
              <Form.Control size="sm" style={{ maxWidth: 220 }} placeholder="ค้นหาชื่อ / ห้อง / วิชา / เขต"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </Card.Body>
          </Card>
        )}

        {error && <Alert variant="danger">{error}</Alert>}
        {ev && !rows && !error && <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>}

        {rows && (
          <Card className="card-one">
            <Card.Header className="d-flex align-items-center gap-2">
              <Card.Title as="h6" className="mb-0">
                {eventName(ev)} · {date ? dayLabel(date, { short: false }) : 'ทุกวัน'}
              </Card.Title>
              <Badge bg="danger" className="ms-auto">{shown.length} รายการ</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    {!date && <th>วันสอบ</th>}
                    <th>ห้อง</th>
                    <th>วิชา</th>
                    <th>เลขประจำตัว</th>
                    <th>ชื่อ-สกุล</th>
                    <th className="text-center">สถานะ</th>
                    <th>หอพัก</th>
                    <th className="d-none d-md-table-cell">ผู้เช็ค</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 ? (
                    <tr><td colSpan={9} className="text-center text-secondary py-4">
                      {rows.length ? 'ไม่พบตามคำค้น' : 'ไม่มีผู้ขาดสอบจากห้องที่เช็คแล้ว'}
                    </td></tr>
                  ) : shown.map((r) => (
                    <tr key={`${r.sub_id}-${r.study_id}`}>
                      {!date && <td className="text-nowrap">{dayLabel(r.date)}</td>}
                      <td className="text-nowrap">{r.room_label}</td>
                      <td className="fs-sm">{r.subject_label}</td>
                      <td>{r.st_idnumber}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <StudentAvatar photo={r.st_photo} name={r.st_name} />
                          <span>
                            {r.st_name}
                            {ev.track === 'islamic' && r.st_arabicname && (
                              <small className="d-block text-secondary exam-arabic" lang="ar" dir="rtl">{r.st_arabicname}</small>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="text-center"><Badge bg={STATUS[r.status].variant}>{STATUS[r.status].long}</Badge></td>
                      <td>{r.village_name ? `${r.village_name} · ${r.dorm_name}` : <span className="text-secondary">ไม่มีทะเบียนหอ</span>}</td>
                      <td className="d-none d-md-table-cell fs-sm text-secondary">{r.checked_by_name}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>
    </>
  );
}
